/**
 * Helpers for mutating the messages infinite-query cache.
 *
 * The inbox reads messages via `useInfiniteQuery`, so the cache shape is
 * `{ pages: MessagePage[], pageParams: unknown[] }` instead of a flat
 * `{ messages: [...] }`. Every optimistic mutation and socket handler
 * that wants to inject/remove/patch a message has to know that shape.
 * Centralize it here so callers stay readable.
 *
 * Page 0 is the newest batch; subsequent pages are older. New messages
 * (real or optimistic) always land at the END of page 0.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { Message } from '@/lib/api/conversations';

export interface MessagePage {
  messages: Message[];
  hasMore: boolean;
  oldestId?: string;
  newestId?: string;
}

export interface MessagesCache {
  pages: MessagePage[];
  pageParams: unknown[];
}

export function mutateMessagesCache(
  queryClient: QueryClient,
  conversationId: string | null | undefined,
  mutator: (pages: MessagePage[]) => MessagePage[]
): void {
  if (!conversationId) return;
  queryClient.setQueryData<MessagesCache>(['messages', conversationId], (old) => {
    if (!old?.pages) return old;
    return { ...old, pages: mutator(old.pages) };
  });
}

export function appendToNewestPage(pages: MessagePage[], message: Message): MessagePage[] {
  if (pages.length === 0) {
    return [{ messages: [message], hasMore: false }];
  }
  const [first, ...rest] = pages;
  // Dedupe — don't append if the message (by id) is already somewhere.
  if (pages.some((p) => p.messages.some((m) => m.id === message.id))) {
    return pages;
  }
  return [{ ...first, messages: [...first.messages, message] }, ...rest];
}

export function updateMessageInPages(
  pages: MessagePage[],
  messageId: string,
  updater: (m: Message) => Message
): MessagePage[] {
  let touched = false;
  const next = pages.map((p) => {
    if (!p.messages.some((m) => m.id === messageId)) return p;
    touched = true;
    return {
      ...p,
      messages: p.messages.map((m) => (m.id === messageId ? updater(m) : m)),
    };
  });
  return touched ? next : pages;
}

export function removeMessageFromPages(
  pages: MessagePage[],
  messageId: string
): MessagePage[] {
  let touched = false;
  const next = pages.map((p) => {
    if (!p.messages.some((m) => m.id === messageId)) return p;
    touched = true;
    return {
      ...p,
      messages: p.messages.filter((m) => m.id !== messageId),
    };
  });
  return touched ? next : pages;
}

export function removeMatchingFromPages(
  pages: MessagePage[],
  predicate: (m: Message) => boolean
): MessagePage[] {
  return pages.map((p) => {
    const kept = p.messages.filter((m) => !predicate(m));
    if (kept.length === p.messages.length) return p;
    return { ...p, messages: kept };
  });
}

/**
 * Swap a temp-* optimistic message for its confirmed server record in
 * place. If the temp id isn't in the cache (e.g. another tab won the
 * race), append the server message to the newest page.
 */
export function replaceTempWithServer(
  pages: MessagePage[],
  tempId: string,
  server: Message
): MessagePage[] {
  let replaced = false;
  const next = pages.map((p) => {
    if (!p.messages.some((m) => m.id === tempId)) return p;
    replaced = true;
    return {
      ...p,
      messages: p.messages.map((m) => (m.id === tempId ? server : m)),
    };
  });
  if (replaced) return next;
  return appendToNewestPage(pages, server);
}
